/**
 * Action Result Display Component
 *
 * Displays the result of a Plaid API action in a formatted view.
 * Shows success with pretty-printed JSON or error details.
 *
 * @fileoverview Display component for Plaid API action results
 *
 * @example
 * <ActionResultDisplay
 *   result={actionResult}
 *   onClear={() => setActionResult(null)}
 * />
 */

import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Box,
  Code,
  Button,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdError,
  MdClear,
  MdAccessTime,
  MdDataObject,
} from 'react-icons/md';

/**
 * Result of a Plaid API action
 */
export interface ActionResult {
  /** Name of the action performed */
  action: string;
  /** Whether the action was successful */
  success: boolean;
  /** Response data from the API */
  data?: unknown;
  /** Error message if action failed */
  error?: string;
  /** Timestamp of when the action was performed */
  timestamp: string;
}

/**
 * Props for ActionResultDisplay component
 */
interface ActionResultDisplayProps {
  /** Action result to display */
  result: ActionResult;
  /** Callback to clear the result */
  onClear: () => void;
}

/**
 * Format timestamp for display
 *
 * @param timestamp - ISO timestamp string
 * @returns Formatted time string
 */
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return 'Unknown';
  }
}

/**
 * Success result display
 */
const SuccessDisplay = ({ result }: { result: ActionResult }) => (
  <>
    {/* @ts-ignore - Chakra UI VStack accepts multiple children */}
    <VStack align="stretch" spacing={4}>
      {/* Success header */}
      <HStack justify="space-between">
        <HStack spacing={2}>
          <Icon as={MdCheckCircle} color="green.400" boxSize={5} />
          <Text color="green.400" fontWeight="semibold">
            {result.action} - Success
          </Text>
        </HStack>
        <Badge colorScheme="green" px={2}>
          200 OK
        </Badge>
      </HStack>

      {/* Response data */}
      {result.data && (
        <Box>
          <HStack spacing={2} mb={2}>
            <Icon as={MdDataObject} color="accent.primary" boxSize={4} />
            <Text color="text.secondary" fontSize="sm" fontWeight="medium">
              Response Data
            </Text>
          </HStack>
          <Box
            bg="bg.primary"
            borderRadius="md"
            p={3}
            maxH="400px"
            overflowY="auto"
            borderLeft="3px solid"
            borderLeftColor="xacto.electricCyan"
          >
            <Code
              bg="transparent"
              color="text.secondary"
              fontSize="xs"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              display="block"
            >
              {JSON.stringify(result.data, null, 2)}
            </Code>
          </Box>
        </Box>
      )}

      {/* Timestamp */}
      <HStack spacing={2}>
        <Icon as={MdAccessTime} color="text.muted" boxSize={3} />
        <Text color="text.muted" fontSize="xs">
          Completed at {formatTimestamp(result.timestamp)}
        </Text>
      </HStack>
    </VStack>
  </>
);

/**
 * Error result display
 */
const ErrorDisplay: React.FC<{ result: ActionResult }> = ({ result }) => (
  <VStack align="stretch" spacing={4}>
    {/* Error header */}
    <HStack spacing={2}>
      <Icon as={MdError} color="red.400" boxSize={5} />
      <Text color="red.400" fontWeight="semibold">
        {result.action} - Failed
      </Text>
    </HStack>

    {/* Error details */}
    <Alert
      status="error"
      bg="red.900"
      borderRadius="md"
      flexDirection="column"
      alignItems="flex-start"
    >
      <HStack spacing={2} mb={2}>
        <AlertIcon />
        <Badge colorScheme="red" fontFamily="mono" fontSize="xs">
          ERROR
        </Badge>
      </HStack>
      <Text color="red.200" fontSize="sm">
        {result.error}
      </Text>
    </Alert>

    {/* Error code for debugging */}
    <Box>
      <Text color="text.muted" fontSize="xs" mb={1}>
        Error Details
      </Text>
      <Code
        bg="bg.primary"
        color="text.secondary"
        p={2}
        borderRadius="md"
        fontSize="xs"
        display="block"
        whiteSpace="pre-wrap"
      >
        {result.error}
      </Code>
    </Box>

    {/* Timestamp */}
    <HStack spacing={2}>
      <Icon as={MdAccessTime} color="text.muted" boxSize={3} />
      <Text color="text.muted" fontSize="xs">
        Failed at {formatTimestamp(result.timestamp)}
      </Text>
    </HStack>
  </VStack>
);

/**
 * Action Result Display Component
 *
 * Displays the result of a Plaid API action.
 * Shows success with JSON data or error details.
 *
 * @param props - Component props
 * @returns Action result display component
 */
export const ActionResultDisplay: React.FC<ActionResultDisplayProps> = ({
  result,
  onClear,
}) => {
  return (
    <VStack align="stretch" spacing={4}>
      {/* Header with clear button */}
      <HStack justify="space-between">
        <Text color="text.primary" fontWeight="semibold" fontSize="sm">
          Action Result
        </Text>
        <Button
          size="xs"
          variant="ghost"
          color="text.muted"
          leftIcon={<Icon as={MdClear} />}
          onClick={onClear}
          _hover={{ color: 'text.secondary', bg: 'bg.muted' }}
        >
          Clear
        </Button>
      </HStack>

      {/* Result content */}
      {result.success ? (
        <SuccessDisplay result={result} />
      ) : (
        <ErrorDisplay result={result} />
      )}
    </VStack>
  );
};

export default ActionResultDisplay;
